import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMessage } from "../context/MessageContext";
import { registerFromInvite } from "../apis/apiList/organizationApi";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { BeatLoader } from "react-spinners";
import "../css/loginCss/Login.css";
import { useApp } from "../context/AppContext";

// Add responsive styles
const responsiveStyles = `
@media (max-width: 768px) {
  .login-content {
    padding: 20px 15px !important;
  }
  .login-header h1 {
    font-size: 24px !important;
  }
  .login-form {
    padding: 15px !important;
  }
  .form-group {
    margin-bottom: 15px !important;
  }
}
@media (max-width: 480px) {
  .login-header h1 {
    font-size: 20px !important;
  }
  .login-form {
    padding: 10px !important;
  }
}
`;

const InviteSignup = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const { showMessage } = useMessage();
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useApp();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    jobTitle: "",
    token: token
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate first name
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    // Validate last name
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    // Validate email
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    // Validate confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Validate job title
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = "Job title is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (validateForm()) {
      try {
        const response = await registerFromInvite(formData);
        if (response.success) {
          showMessage("Registration successful! You can now log in.", "success");
          // navigate("/login");
          login(response.user);
          if (response.user.role === 'admin') {
            navigate("/task/list");
          } else {
            if (response.user.orgId) {
              navigate(`/subs/org/${response.user.orgId}/projects`);
            } else {
              navigate("/select-organization");
            }
          }
        }
      } catch (err) {
        showMessage(err?.response?.data?.message || "Registration failed. Please try again.", "error");
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <style>{responsiveStyles}</style>
      <Header />
      
      <section className="login-section">
        <div className="container">
          <div className="login-wrapper" style={{ gridTemplateColumns: '1fr', paddingTop: '20px', paddingBottom: '20px' }}>
            <div className="login-content" style={{ width: '100%', maxWidth: '800px', margin: '0 auto', padding: '0 15px' }}>
              <div className="login-header">
                <h1>Join Organization</h1>
                <p style={{textAlign: 'center'}}>Complete your account setup to get started</p>
              </div>

              <div className="login-form-container">
                <form onSubmit={handleSubmit} className="login-form">
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                    gap: '20px' 
                  }}>
                    <div className="form-group">
                      <label htmlFor="firstName">First Name</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={errors.firstName ? "error" : ""}
                        placeholder="Enter your first name"
                      />
                      {errors.firstName && <div className="error-message">{errors.firstName}</div>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="lastName">Last Name</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={errors.lastName ? "error" : ""}
                        placeholder="Enter your last name"
                      />
                      {errors.lastName && <div className="error-message">{errors.lastName}</div>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? "error" : ""}
                      placeholder="Enter your email address"
                    />
                    {errors.email && <div className="error-message">{errors.email}</div>}
                  </div>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                    gap: '20px' 
                  }}>
                    <div className="form-group">
                      <label htmlFor="password">Password</label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={errors.password ? "error" : ""}
                        placeholder="Create a password"
                      />
                      {errors.password && <div className="error-message">{errors.password}</div>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">Confirm Password</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={errors.confirmPassword ? "error" : ""}
                        placeholder="Confirm your password"
                      />
                      {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="jobTitle">Job Title</label>
                    <input
                      type="text"
                      id="jobTitle"
                      name="jobTitle"
                      value={formData.jobTitle}
                      onChange={handleChange}
                      className={errors.jobTitle ? "error" : ""}
                      placeholder="Enter your job title"
                    />
                    {errors.jobTitle && <div className="error-message">{errors.jobTitle}</div>}
                  </div>

                  <button 
                    type="submit" 
                    className="btn-primary" 
                    disabled={isLoading}
                    style={{ 
                      padding: '15px 30px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: '10px',
                      width: '100%',
                      maxWidth: '100%'
                    }}
                  >
                    {isLoading ? (
                      <>
                        <BeatLoader size={8} color="#ffffff" />
                        <span>Creating Account...</span>
                      </>
                    ) : (
                      "Complete Registration"
                    )}
                  </button>

                  <div className="form-footer" style={{ textAlign: 'center', marginTop: '20px' }}>
                    <span>Already have an account? <a href="/login" className="link">Sign in</a></span>
                  </div>
                </form>
              </div>
            </div>

            {/* Right side visual content commented out as requested
            <div className="login-visual">
              <div className="visual-content">
                <h2>Welcome to Snapshot Videos</h2>
                <p>Join your organization and start creating professional video content that keeps your members informed and engaged.</p>
                <div className="features">
                  <div className="feature-item">
                    <div className="feature-icon">🎥</div>
                    <span>Professional Video Production</span>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon">⚡</div>
                    <span>Quick Turnaround</span>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon">👥</div>
                    <span>Member Engagement</span>
                  </div>
                </div>
              </div>
            </div>
            */}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InviteSignup;